import { Button } from '@/components/ui/button';
import { BREAKPOINTS } from '@/constants';
import { useMatchMedia } from '@/hooks/useMatchMedia';
import { MOTION } from '@/utils';
import { withBasePath } from '@/utils/basePath';
import { PRICING_FUNCTIONS } from '@/utils/data/princingFunctions';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import { useRef } from 'react';

function PricingRow({ children, isNew = false }: { children: string; isNew?: boolean }) {
  return (
    <div className="flex w-full items-center gap-2">
      <Image
        alt="icone de validation"
        className="h-4 w-4"
        decoding="async"
        height={16}
        loading="lazy"
        src={withBasePath('/images/logos/checkIcon.svg')}
        width={16}
      />
      <p className="w-full text-clip">
        {isNew && <span className="text-orange font-bold!">(New) </span>}
        {children}
      </p>
    </div>
  );
}

export default function Pricing() {
  const isMobile = useMatchMedia(BREAKPOINTS.MD);
  const refs = {
    section: useRef(null),
    title: useRef(null),
    subtitle: useRef(null),
    card: useRef(null),
    price: useRef(null),
    counter: useRef<HTMLSpanElement | null>(null),
    engagement: useRef(null),
    cta: useRef(null),
    leftRows: useRef<HTMLDivElement[]>([]),
    rightRows: useRef<HTMLDivElement[]>([]),
    units: {
      one: useRef(null),
      two: useRef(null),
    },
  };

  useGSAP(
    () => {
      if (!refs.section.current) return;

      const splitTitle = new SplitText(refs.title.current, {
        type: 'lines',
        mask: 'lines',
      });
      const splitSubtitle = new SplitText(refs.subtitle.current, {
        type: 'lines',
        mask: 'lines',
      });
      const splitEngagement = new SplitText(refs.engagement.current, {
        type: 'lines',
        mask: 'lines',
      });

      const pairedRows = Array.from(
        {
          length: Math.max(refs.leftRows.current.length, refs.rightRows.current.length),
        },
        (_, index) => [refs.leftRows.current[index], refs.rightRows.current[index]].filter(Boolean),
      );
      const rowStagger = 0.06;
      const mobileRows = [...refs.leftRows.current, ...refs.rightRows.current];

      const counterValue = { value: 0 };

      const tl = gsap.timeline({
        defaults: { duration: MOTION.durationMd, ease: MOTION.ease },
        scrollTrigger: {
          trigger: refs.section.current,
          start: 'top 60%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.from(splitTitle.lines, {
        yPercent: MOTION.yPercent,
        stagger: MOTION.staggerLines,
      })
        .from(
          splitSubtitle.lines,
          {
            yPercent: MOTION.yPercent,
            stagger: MOTION.staggerLines,
          },
          '<+0.2',
        )
        .from(
          refs.card.current,
          {
            autoAlpha: 0,
            y: MOTION.y,
          },
          '<+0.2',
        )
        .from(
          refs.price.current,
          {
            autoAlpha: 0,
            scale: 0.8,
            duration: 0.5,
          },
          '-=0.2',
        )
        .to(
          counterValue,
          {
            value: 49,
            duration: 1,
            ease: 'power2.out',
            onUpdate: () => {
              if (refs.counter.current) {
                refs.counter.current.textContent = Math.round(counterValue.value).toString();
              }
            },
          },
          '<',
        )
        .from(
          [refs.units.one.current, refs.units.two.current],
          {
            autoAlpha: 0,
            x: -10,
            stagger: 0.1,
          },
          '<+0.2',
        )
        .from(
          splitEngagement.lines,
          {
            yPercent: MOTION.yPercent,
          },
          '<',
        )
        .from(
          refs.cta.current,
          {
            autoAlpha: 0,
            yPercent: MOTION.y,
          },
          '<',
        );

      if (isMobile) {
        tl.from(
          mobileRows,
          {
            autoAlpha: 0,
            yPercent: MOTION.y,
            stagger: rowStagger,
          },
          '<+0.06',
        );
      } else {
        pairedRows.forEach((pair, index) => {
          tl.from(
            pair,
            {
              autoAlpha: 0,
              yPercent: MOTION.y,
              stagger: pair.length > 1 ? rowStagger : 0,
            },
            index === 0 ? '-=0.2' : '<+0.08',
          );
        });
      }
    },
    {
      dependencies: [isMobile],
      scope: refs.section,
      revertOnUpdate: true,
    },
  );

  return (
    <section
      ref={refs.section}
      className="max-width-blue-section flex flex-col items-center gap-8"
      id="tarif"
    >
      <div className="flex flex-col items-center justify-center">
        <h2 ref={refs.title} className="text-center">
          Un tarif unique, transparent et accessible
        </h2>
        <p className="sr-only">
          Avec zLawyer, pas de mauvaise surprise : 1 logiciel, un tarif par utilisateur par mois
          avec toutes les fonctionnalités incluses.
        </p>

        <p ref={refs.subtitle} aria-hidden="true" className="max-w-3xl text-center">
          Avec zLawyer, pas de mauvaise surprise : 1 logiciel, un tarif par utilisateur par mois
          avec toutes les fonctionnalites incluses.
        </p>
      </div>

      <div
        ref={refs.card}
        className="flex flex-col gap-10 rounded-(--radius-current) bg-white p-10 [box-shadow:var(--shadow-current)]"
      >
        <div className="flex flex-col items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="flex gap-4">
              <p ref={refs.price} className="text-orange w-28 text-center text-6xl font-bold!">
                <span ref={refs.counter}>0</span>€
              </p>
              <div className="flex flex-col">
                <p ref={refs.units.one} className="text-orange text-2xl font-bold!">
                  /mois
                </p>
                <p ref={refs.units.two} className="text-orange text-2xl font-bold!">
                  /utilisateur
                </p>
              </div>
            </div>
            <p ref={refs.engagement} className="text-orange text-sm">
              et sans engagement
            </p>
          </div>

          <Button
            ref={refs.cta}
            className="mt-6 w-full text-center md:w-1/2"
            href="/contact"
            variant="fill"
          >
            Demandez une demo
          </Button>
        </div>

        <div className="flex w-full flex-col gap-2 md:flex-row md:gap-4">
          <div className="flex h-full flex-col gap-2">
            {PRICING_FUNCTIONS.left.map((item, index) => (
              <div
                key={item}
                ref={(element) => {
                  if (element) refs.leftRows.current[index] = element;
                }}
              >
                <PricingRow>{item}</PricingRow>
              </div>
            ))}
          </div>

          <div className="flex h-full flex-col gap-2">
            {PRICING_FUNCTIONS.right.map((item, index) => (
              <div
                key={item}
                ref={(element) => {
                  if (element) refs.rightRows.current[index] = element;
                }}
              >
                <PricingRow isNew={item === 'Intelligence artificielle Zephyr'}>{item}</PricingRow>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
